import { DesktopOutlined, MoonOutlined, SunOutlined } from '@ant-design/icons';
import { Segmented, Tooltip } from 'antd';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useThemeMode, type ThemeChoice } from '../lib/theme-mode';

/**
 * System / Light / Dark, in the top bar (reference §6).
 *
 * Three-way rather than a toggle because "system" is a real answer, not the
 * absence of one — somebody whose laptop flips to dark at sunset expects the
 * app to follow, and a two-way control cannot express that.
 *
 * Icons only: it sits next to the branch switcher and the sign-out menu, and
 * three more words of chrome in a bar that is 56px tall is three too many.
 */
export function ThemeToggle(): ReactNode {
  const { t } = useTranslation();
  const { choice, setChoice } = useThemeMode();

  const options: { value: ThemeChoice; icon: ReactNode; label: string }[] = [
    { value: 'system', icon: <DesktopOutlined />, label: t('theme.system') },
    { value: 'light', icon: <SunOutlined />, label: t('theme.light') },
    { value: 'dark', icon: <MoonOutlined />, label: t('theme.dark') },
  ];

  return (
    <Segmented<ThemeChoice>
      value={choice}
      onChange={setChoice}
      size="small"
      aria-label={t('theme.label')}
      options={options.map((option) => ({
        value: option.value,
        label: (
          <Tooltip title={option.label}>
            <span aria-label={option.label} role="img">
              {option.icon}
            </span>
          </Tooltip>
        ),
      }))}
    />
  );
}
